import React, { useRef, useState } from "react";
import PropType from "prop-types";
import { SignedImage } from "../common/SignedImage";

const SectionCard = ({ title, children }) => (
  <div className="card shadow-sm mb-4">
    <div className="card-header bg-white border-bottom py-3">
      <h6 className="mb-0 fw-semibold text-dark">{title}</h6>
    </div>
    <div className="card-body px-3 py-3">{children}</div>
  </div>
);

SectionCard.propTypes = {
  title: PropType.string.isRequired,
  children: PropType.node.isRequired,
};

function EditPersonalDetails({
  userProfile,
  saveHandler,
  changeHandler,
  uploadProfileImageHandler,
  formError,
  formErrorMessage,
  cancelHandler,
}) {
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [pendingFileName, setPendingFileName] = useState(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setPendingFileName(e.target.files[0].name);
      uploadProfileImageHandler(e);
    }
  };

  const isVatRegistered = userProfile.vatRegistered === true || userProfile.vatRegistered === "true";
  const labelClass = "form-label small fw-semibold text-muted text-uppercase";
  const labelStyle = { letterSpacing: "0.05em" };

  return (
    <div>
      {formError && (
        <div className="alert alert-danger mb-4" role="alert">
          <p className="fw-semibold mb-2">Please fix the following errors:</p>
          <ul className="mb-0 ps-3">
            {formErrorMessage.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={saveHandler}>
        {/* Profile image */}
        <div className="text-center mb-4">
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="d-none" onChange={handleFileChange} />
          <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleFileChange} />

          {userProfile.engineerImageUrl ? (
            <SignedImage
              src={userProfile.engineerImageUrl}
              alt="Profile"
              className="rounded-circle border shadow-sm mb-3"
              style={{ width: 100, height: 100, objectFit: "cover" }}
            />
          ) : (
            <div
              className="rounded-circle bg-secondary d-inline-flex align-items-center justify-content-center border shadow-sm mb-3"
              style={{ width: 100, height: 100 }}
            >
              <span className="text-white fs-3 fw-bold">
                {userProfile.firstName ? userProfile.firstName.charAt(0).toUpperCase() : "?"}
              </span>
            </div>
          )}

          <p className={`${labelClass} mb-2`} style={labelStyle}>
            Profile Photo
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => cameraInputRef.current.click()}>
              Camera
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => fileInputRef.current.click()}>
              Upload File
            </button>
          </div>
          {pendingFileName && (
            <p className="text-muted small mt-2 fst-italic mb-0">{pendingFileName}</p>
          )}
        </div>

        <div className="row justify-content-center">
          <div className="col-12 col-md-6">
            <SectionCard title="Personal Information">
              <div className="form-group mb-3">
                <label htmlFor="title" className={labelClass} style={labelStyle}>
                  Title
                </label>
                <select className="form-select" onChange={changeHandler} name="title" defaultValue={userProfile.title} id="title">
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Miss">Miss</option>
                  <option value="Ms">Ms</option>
                  <option value="Dr">Dr</option>
                </select>
              </div>
              <div className="row g-3 mb-3">
                <div className="col-6">
                  <label htmlFor="firstName" className={labelClass} style={labelStyle}>
                    First Name
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    name="firstName"
                    onChange={changeHandler}
                    id="firstName"
                    value={userProfile.firstName}
                    required
                  />
                </div>
                <div className="col-6">
                  <label htmlFor="lastName" className={labelClass} style={labelStyle}>
                    Last Name
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    name="lastName"
                    onChange={changeHandler}
                    id="lastName"
                    value={userProfile.lastName}
                  />
                </div>
              </div>
              <div className="form-group mb-3">
                <label htmlFor="email" className={labelClass} style={labelStyle}>
                  Email
                </label>
                <input
                  className="form-control"
                  type="email"
                  name="email"
                  onChange={changeHandler}
                  id="email"
                  value={userProfile.email}
                />
              </div>
              <div className="form-group mb-3">
                <label htmlFor="mobileNumber" className={labelClass} style={labelStyle}>
                  Mobile Number
                </label>
                <input
                  className="form-control"
                  type="tel"
                  name="mobileNumber"
                  onChange={changeHandler}
                  id="mobileNumber"
                  value={userProfile.mobileNumber}
                  maxLength={13}
                  minLength={11}
                />
              </div>
              <div className="form-group mb-0">
                <label htmlFor="inputGasSafeNumber" className={labelClass} style={labelStyle}>
                  Gas Safe Licence Number
                </label>
                <input
                  className="form-control"
                  type="text"
                  name="gasSafeLicenseNumber"
                  onChange={changeHandler}
                  id="inputGasSafeNumber"
                  value={userProfile.gasSafeLicenseNumber}
                />
              </div>
            </SectionCard>
          </div>

          <div className="col-12 col-md-6">
            <SectionCard title="Company Information">
              <div className="form-group mb-3">
                <label htmlFor="companyName" className={labelClass} style={labelStyle}>
                  Company Name
                </label>
                <input
                  className="form-control"
                  type="text"
                  name="companyName"
                  onChange={changeHandler}
                  id="companyName"
                  value={userProfile.companyName}
                />
              </div>

              <p className={`${labelClass} mb-2`} style={labelStyle}>
                Address
              </p>
              <div className="border rounded p-3 mb-3">
                {[
                  { id: "address1", label: "Line 1", name: "address1", value: userProfile.address1 },
                  { id: "address2", label: "Line 2", name: "address2", value: userProfile.address2 },
                  { id: "address3", label: "Line 3", name: "address3", value: userProfile.address3 },
                  { id: "city", label: "City", name: "city", value: userProfile.city },
                  { id: "county", label: "County", name: "county", value: userProfile.county },
                  { id: "postcode", label: "Postcode", name: "postcode", value: userProfile.postcode },
                ].map(({ id, label, name, value }) => (
                  <div className="row align-items-center mb-2" key={id}>
                    <label htmlFor={id} className="col-4 col-form-label small text-muted">
                      {label}
                    </label>
                    <div className="col-8">
                      <input className="form-control form-control-sm" type="text" name={name} onChange={changeHandler} id={id} value={value} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-group mb-3">
                <label htmlFor="companyGasSafeNumber" className={labelClass} style={labelStyle}>
                  Gas Safe Number
                </label>
                <input
                  className="form-control"
                  type="text"
                  name="companyGasSafeNumber"
                  id="companyGasSafeNumber"
                  value={userProfile.companyGasSafeNumber}
                  onChange={changeHandler}
                />
              </div>

              <div className="form-group mb-3">
                <p className={`${labelClass} mb-2`} style={labelStyle}>
                  Company Type
                </p>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="companyType"
                      id="radioCompanyTypeSole"
                      value="soleTrader"
                      onChange={changeHandler}
                      checked={userProfile.companyType === "soleTrader"}
                    />
                    <label className="form-check-label" htmlFor="radioCompanyTypeSole">
                      Sole Trader
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="companyType"
                      id="radioCompanyTypeLimited"
                      value="limitedCompany"
                      onChange={changeHandler}
                      checked={userProfile.companyType === "limitedCompany"}
                    />
                    <label className="form-check-label" htmlFor="radioCompanyTypeLimited">
                      Limited Company
                    </label>
                  </div>
                </div>
              </div>

              {userProfile.companyType === "limitedCompany" && (
                <div className="form-group mb-3">
                  <label htmlFor="companyNumber" className={labelClass} style={labelStyle}>
                    Limited Company Number
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    name="companyNumber"
                    id="companyNumber"
                    value={userProfile.companyNumber}
                    onChange={changeHandler}
                  />
                </div>
              )}

              <div className="form-group mb-3">
                <p className={`${labelClass} mb-2`} style={labelStyle}>
                  VAT Registered
                </p>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="vatRegistered"
                      id="radioVatRegisteredYes"
                      value="true"
                      onChange={changeHandler}
                      checked={isVatRegistered}
                    />
                    <label className="form-check-label" htmlFor="radioVatRegisteredYes">
                      Yes
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="vatRegistered"
                      id="radioVatRegisteredNo"
                      value="false"
                      onChange={changeHandler}
                      checked={!isVatRegistered}
                    />
                    <label className="form-check-label" htmlFor="radioVatRegisteredNo">
                      No
                    </label>
                  </div>
                </div>
              </div>

              {isVatRegistered && (
                <div className="form-group mb-0">
                  <label htmlFor="vatNumber" className={labelClass} style={labelStyle}>
                    VAT Number
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    name="vatNumber"
                    id="vatNumber"
                    value={userProfile.vatNumber}
                    onChange={changeHandler}
                  />
                </div>
              )}
            </SectionCard>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-12 col-md-6 col-lg-4 d-grid gap-2">
            <button type="submit" className="btn btn-primary" onClick={saveHandler}>
              Save Changes
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={cancelHandler}>
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

EditPersonalDetails.propTypes = {
  userProfile: PropType.object.isRequired,
  saveHandler: PropType.func.isRequired,
  changeHandler: PropType.func.isRequired,
  uploadProfileImageHandler: PropType.func.isRequired,
  formError: PropType.bool.isRequired,
  formErrorMessage: PropType.array.isRequired,
  cancelHandler: PropType.func.isRequired,
};

export default EditPersonalDetails;
